import React, { useState, useMemo } from "react";
import { useI18n } from "../../lib/i18n";
import { Button } from "../ui/button";
import { DownloadSimple, Copy, CheckCircle, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

// ---------------------------------------------------------------------------
// Code generators — receive the full project object, return file content
// ---------------------------------------------------------------------------

/** Phaser 3 export: combines user files + sprite manifest + scene bootstrap */
function generatePhaser(project) {
  const slug = project.slug || "my-game";
  const name = project.name || "My Game";
  const entities = project.gdd?.entities || [];
  const sprites = project.sprites || [];
  const controls = project.controls?.keyboard || {};
  const files = project.files || [];

  const up    = controls.move_up    || "W";
  const left  = controls.move_left  || "A";
  const down  = controls.move_down  || "S";
  const right = controls.move_right || "D";
  const jump  = controls.jump       || "Space";

  const playerEntity = entities.find(e =>
    ["player", "hero", "protagonist"].includes((e.name || "").toLowerCase()) ||
    (e.type || "").toLowerCase() === "player"
  );
  const enemyEntities = entities.filter(e =>
    ["enemy", "boss"].includes((e.type || "").toLowerCase())
  );
  const playerHp   = playerEntity?.attributes?.hp || 100;
  const playerSpd  = playerEntity?.attributes?.speed || 200;

  // sprite base64 declarations
  const spriteConsts = sprites.map((s, i) => {
    const key = (s.name || `sprite${i}`).replace(/\s+/g, "_");
    return `// Sprite: ${key}\n// source: project.sprites[${i}].data_url  (base64 PNG)\nconst ${key.toUpperCase()}_KEY = '${key}';`;
  }).join("\n") || "// No sprites saved yet. Draw them in the Sprite Editor.";

  const preloadSprites = sprites.map((s, i) => {
    const key = (s.name || `sprite${i}`).replace(/\s+/g, "_");
    return `    this.textures.addBase64('${key}', SPRITE_DATA['${key}']);`;
  }).join("\n") || "    // add sprites here";

  // user code inlined
  const userCode = files.length > 0
    ? files.map(f => `// ── ${f.path} ──\n${f.content || ""}`).join("\n\n")
    : "// Your game logic here";

  // GDD entity comments
  const entityComments = entities.map(e =>
    ` * ${e.name} (${e.type || "entity"}): HP=${e.attributes?.hp ?? "?"} SPD=${e.attributes?.speed ?? "?"}`
  ).join("\n") || " * No entities defined yet";

  return `// ============================================================
// ${name} — Phaser 3 Export
// Generated by GameForge from project: ${slug}
// ============================================================

/**
 * GDD Entities:
${entityComments}
 */

// Sprite keys (load data_url values from your project export)
${spriteConsts}

// Sprite data map — populate from project.sprites[].data_url
const SPRITE_DATA = {
${sprites.map((s,i) => `  '${(s.name||`sprite${i}`).replace(/\s+/g,"_")}': '${s.data_url ? s.data_url.slice(0,40)+"...(base64)..." : "/* paste base64 here */"}'`).join(",\n")}
};

// ── User Code ──────────────────────────────────────────────────
${userCode}
// ──────────────────────────────────────────────────────────────

class BootScene extends Phaser.Scene {
  constructor() { super({ key: 'Boot' }); }

  preload() {
${preloadSprites}
  }

  create() {
    this.scene.start('Game');
  }
}

class GameScene extends Phaser.Scene {
  constructor() { super({ key: 'Game' }); }

  create() {
    const W = this.scale.width;
    const H = this.scale.height;

    // Ground
    this.platforms = this.physics.add.staticGroup();
    const ground = this.add.rectangle(W/2, H - 16, W, 32, 0x334155);
    this.physics.add.existing(ground, true);
    this.platforms.add(ground);

    // Player — '${playerEntity?.name || "Player"}'
    const pKey = ${sprites.length > 0 ? `'${(sprites[0].name || "sprite0").replace(/\s+/g,"_")}'` : "null"};
    this.player = this.physics.add.sprite(100, H - 60, pKey || '__fallback');
    this.player.setCollideWorldBounds(true);
    this.player.hp = ${playerHp};
    this.player.speed = ${playerSpd};

    // Enemies (${enemyEntities.length} from GDD)
${enemyEntities.length === 0
  ? "    // Define enemies in your GDD to auto-generate spawn code here"
  : enemyEntities.map((e, i) =>
    `    // Enemy: ${e.name}\n    // this.enemies.add(this.physics.add.sprite(${200 + i * 80}, H - 60, 'enemy_texture'));`
  ).join("\n")}

    this.physics.add.collider(this.player, this.platforms);

    // Controls
    this.keys = this.input.keyboard.addKeys({
      up:    '${up}',
      down:  '${down}',
      left:  '${left}',
      right: '${right}',
      jump:  '${jump === "Space" ? "SPACE" : jump}',
    });
  }

  update() {
    const onGround = this.player.body.blocked.down;

    if (this.keys.left.isDown) {
      this.player.setVelocityX(-this.player.speed);
    } else if (this.keys.right.isDown) {
      this.player.setVelocityX(this.player.speed);
    } else {
      this.player.setVelocityX(0);
    }

    if ((this.keys.jump.isDown || this.keys.up.isDown) && onGround) {
      this.player.setVelocityY(-520);
    }
  }
}

const config = {
  type: Phaser.AUTO,
  width: 800,
  height: 480,
  backgroundColor: '#0f172a',
  physics: {
    default: 'arcade',
    arcade: { gravity: { y: 500 }, debug: false },
  },
  scene: [BootScene, GameScene],
};

const game = new Phaser.Game(config);
`;
}

/** Godot 4 (GDScript) export */
function generateGodot(project) {
  const name  = project.name  || "MyGame";
  const slug  = project.slug  || "my-game";
  const entities  = project.gdd?.entities || [];
  const sprites   = project.sprites || [];
  const controls  = project.controls?.keyboard || {};
  const files     = project.files || [];

  const up    = controls.move_up    || "W";
  const left  = controls.move_left  || "A";
  const down  = controls.move_down  || "S";
  const right = controls.move_right || "D";
  const jump  = controls.jump       || "Space";

  const playerEntity = entities.find(e =>
    (e.type || "").toLowerCase() === "player" ||
    ["player","hero"].includes((e.name || "").toLowerCase())
  );
  const hp    = playerEntity?.attributes?.hp   || 100;
  const speed = playerEntity?.attributes?.speed || 200;

  const enemyEntities = entities.filter(e =>
    ["enemy","boss"].includes((e.type||"").toLowerCase())
  );

  const userGDCode = files
    .filter(f => f.path.endsWith(".gd"))
    .map(f => `# ── ${f.path} ──\n${f.content || ""}`)
    .join("\n\n") || "# Your GDScript files will appear here.";

  const entityDocs = entities.map(e =>
    `# ${e.name} (${e.type||"entity"}): hp=${e.attributes?.hp ?? "?"} speed=${e.attributes?.speed ?? "?"} desc=${e.description || "—"}`
  ).join("\n") || "# No entities in GDD yet";

  const spriteDocs = sprites.map((s,i) =>
    `# Sprite[${i}]: ${s.name || "unnamed"} → import as '${(s.name||`sprite${i}`).replace(/\s+/g,"_")}.png'`
  ).join("\n") || "# No sprites saved — draw them in the Sprite Editor";

  const enemyCode = enemyEntities.length === 0
    ? "# Add enemy entities in the GDD to auto-generate enemy nodes"
    : enemyEntities.map(e => {
      const eName = (e.name || "Enemy").replace(/\s+/g, "");
      return `
# --- Enemy: ${e.name} (HP=${e.attributes?.hp ?? 3}) ---
# Attach this as a separate scene: res://enemies/${eName}.tscn
# extends CharacterBody2D
# const HP = ${e.attributes?.hp ?? 3}
# const SPEED = ${e.attributes?.speed ?? 80}`;
    }).join("\n");

  return `# ============================================================
# ${name} — Godot 4 GDScript Export
# Generated by GameForge · project: ${slug}
# ============================================================
# Godot version: 4.x
# Import instructions:
#   1. Create a new Godot 4 project.
#   2. Copy this file as res://scripts/Player.gd
#   3. Attach it to a CharacterBody2D node named "Player"
#   4. Import sprite PNGs (see list below) to res://sprites/
#   5. Assign the texture to the Sprite2D child node.
#   6. Add InputMap actions in Project Settings → Input Map:
#      move_left, move_right, move_up, move_down, jump
# ============================================================

# ── GDD Entities ─────────────────────────────────────────────
${entityDocs}

# ── Sprites to import ────────────────────────────────────────
${spriteDocs}

# ── Your .gd files ───────────────────────────────────────────
${userGDCode}

# ── Player Controller ────────────────────────────────────────
extends CharacterBody2D

# From GDD entity: ${playerEntity?.name || "Player"}
const HP: int    = ${hp}
const SPEED: float = ${speed}.0
const JUMP_VELOCITY: float = -${Math.round(speed * 2.2)}

@export var gravity: float = ProjectSettings.get_setting("physics/2d/default_gravity")

var current_hp: int = HP

func _ready() -> void:
\tprint("[${name}] Player ready · HP=%d SPEED=%d" % [HP, SPEED])

func _physics_process(delta: float) -> void:
\t# Gravity
\tif not is_on_floor():
\t\tvelocity.y += gravity * delta

\t# Jump — mapped to '${jump}' in GameForge
\tif Input.is_action_just_pressed("jump") and is_on_floor():
\t\tvelocity.y = JUMP_VELOCITY

\t# Horizontal movement
\tvar direction := Input.get_axis("move_left", "move_right")
\tif direction:
\t\tvelocity.x = direction * SPEED
\telse:
\t\tvelocity.x = move_toward(velocity.x, 0, SPEED)

\tmove_and_slide()

func take_damage(amount: int) -> void:
\tcurrent_hp -= amount
\tif current_hp <= 0:
\t\tdie()

func die() -> void:
\tprint("Player died")
\tget_tree().reload_current_scene()

# ── Input Map (for Project Settings → Input Map) ─────────────
# Action      Key
# move_up     ${up}
# move_down   ${down}
# move_left   ${left}
# move_right  ${right}
# jump        ${jump}

${enemyCode}

# ── project.tres snippet (paste into your .godot project) ────
# [node name="Player" type="CharacterBody2D"]
# script = ExtResource("res://scripts/Player.gd")
# [node name="Sprite2D" type="Sprite2D" parent="."]
# texture = ExtResource("res://sprites/${(sprites[0]?.name||"player").replace(/\s+/g,"_")}.png")
# [node name="CollisionShape2D" type="CollisionShape2D" parent="."]
# shape = RectangleShape2D
`;
}

/** Unity C# export */
function generateUnity(project) {
  const name      = project.name || "MyGame";
  const slug      = project.slug || "my-game";
  const entities  = project.gdd?.entities || [];
  const sprites   = project.sprites || [];
  const controls  = project.controls?.keyboard || {};
  const files     = project.files || [];

  const up    = controls.move_up    || "W";
  const left  = controls.move_left  || "A";
  const right = controls.move_right || "D";
  const jump  = controls.jump       || "Space";

  const playerEntity = entities.find(e =>
    (e.type||"").toLowerCase() === "player" ||
    ["player","hero"].includes((e.name||"").toLowerCase())
  );
  const hp    = playerEntity?.attributes?.hp   || 100;
  const speed = playerEntity?.attributes?.speed || 5;

  const enemyEntities = entities.filter(e =>
    ["enemy","boss"].includes((e.type||"").toLowerCase())
  );

  const userCSCode = files
    .filter(f => f.path.endsWith(".cs"))
    .map(f => `// ── ${f.path} ──\n${f.content || ""}`)
    .join("\n\n") || "// Your .cs files will appear here.";

  const entityDocs = entities.map(e =>
    `// ${e.name} (${e.type||"entity"}): HP=${e.attributes?.hp??0} Speed=${e.attributes?.speed??0}`
  ).join("\n") || "// No entities in GDD";

  const spriteDocs = sprites.map((s,i) =>
    `// Sprite[${i}]: ${s.name||"unnamed"} → Assets/Sprites/${(s.name||`sprite${i}`).replace(/\s+/g,"_")}.png`
  ).join("\n") || "// No sprites — draw them in the Sprite Editor first";

  const enemyClasses = enemyEntities.map(e => {
    const cn = (e.name || "Enemy").replace(/[^a-zA-Z0-9]/g,"");
    return `
// ── Enemy: ${e.name} ────────────────────────────────────────
public class ${cn}Controller : MonoBehaviour
{
    public int   hp    = ${e.attributes?.hp   ?? 3};
    public float speed = ${e.attributes?.speed ?? 2}f;
    private int  direction = 1;

    void Update()
    {
        transform.Translate(Vector3.right * direction * speed * Time.deltaTime);
    }

    void OnCollisionEnter2D(Collision2D col)
    {
        if (col.collider.CompareTag("Wall")) direction *= -1;
        if (col.collider.CompareTag("Player"))
            col.collider.GetComponent<PlayerController>()?.TakeDamage(1);
    }

    public void TakeDamage(int dmg)
    {
        hp -= dmg;
        if (hp <= 0) Destroy(gameObject);
    }
}`;
  }).join("\n");

  const jumpKey = jump === "Space" ? "KeyCode.Space"
    : `KeyCode.${jump.charAt(0).toUpperCase() + jump.slice(1)}`;

  return `// ============================================================
// ${name} — Unity 2D C# Export
// Generated by GameForge · project: ${slug}
// ============================================================
// Unity version: 2022 LTS or 6 (URP or Built-in)
// Setup:
//   1. New Unity 2D project.
//   2. Copy PlayerController.cs → Assets/Scripts/
//   3. Import sprite PNGs → Assets/Sprites/ (see list)
//   4. Attach PlayerController to a GameObject with Rigidbody2D + BoxCollider2D
// ============================================================

using UnityEngine;

// ── GDD Entities ─────────────────────────────────────────────
${entityDocs}

// ── Sprites to import ────────────────────────────────────────
${spriteDocs}

// ── Your C# files ────────────────────────────────────────────
${userCSCode}

// ── Player Controller ────────────────────────────────────────
// GDD Entity: ${playerEntity?.name || "Player"}
public class PlayerController : MonoBehaviour
{
    [Header("Stats from GDD")]
    public int   maxHp    = ${hp};
    public float speed    = ${Math.min(speed, 20)}f;
    public float jumpForce = ${Math.round(Math.min(speed, 20) * 40)}f;

    private int          currentHp;
    private Rigidbody2D  rb;
    private bool         isGrounded;
    private float        iFrameTimer;
    private const float  IFRAME_DUR = 1f;

    // Input keys (from GameForge controls)
    private KeyCode keyLeft  = KeyCode.${left.toUpperCase()[0] + left.slice(1)};
    private KeyCode keyRight = KeyCode.${right.toUpperCase()[0] + right.slice(1)};
    private KeyCode keyJump  = ${jumpKey};

    void Start()
    {
        rb        = GetComponent<Rigidbody2D>();
        currentHp = maxHp;
        Debug.Log($"[${name}] Player start · HP={maxHp} Speed={speed}");
    }

    void Update()
    {
        if (iFrameTimer > 0) iFrameTimer -= Time.deltaTime;

        float h = 0f;
        if (Input.GetKey(keyLeft))  h = -1f;
        if (Input.GetKey(keyRight)) h =  1f;
        rb.velocity = new Vector2(h * speed, rb.velocity.y);

        if (Input.GetKeyDown(keyJump) && isGrounded)
            rb.AddForce(Vector2.up * jumpForce, ForceMode2D.Impulse);

        // Flip sprite
        if (h != 0)
            transform.localScale = new Vector3(Mathf.Sign(h), 1f, 1f);
    }

    void OnCollisionEnter2D(Collision2D col)
    {
        if (col.contacts[0].normal.y > 0.5f) isGrounded = true;
    }
    void OnCollisionExit2D(Collision2D col)  { isGrounded = false; }

    public void TakeDamage(int dmg)
    {
        if (iFrameTimer > 0) return;
        currentHp -= dmg;
        iFrameTimer = IFRAME_DUR;
        if (currentHp <= 0) Die();
    }

    void Die()
    {
        Debug.Log("Player died");
        UnityEngine.SceneManagement.SceneManager.LoadScene(
            UnityEngine.SceneManagement.SceneManager.GetActiveScene().name);
    }
}
${enemyClasses.length ? enemyClasses : "\n// Add enemies in your GDD to generate enemy classes here"}
`;
}

// ---------------------------------------------------------------------------
// ZIP-like export: creates a single downloadable HTML that bundles Phaser game
// ---------------------------------------------------------------------------
function generateStandaloneHTML(project) {
  const name = project.name || "Game";
  const sprites = project.sprites || [];
  const files = project.files || [];
  const controls = project.controls?.keyboard || {};
  const entities = project.gdd?.entities || [];

  const up    = controls.move_up    || "W";
  const left  = controls.move_left  || "A";
  const right = controls.move_right || "D";
  const jump  = controls.jump       || "Space";

  const spriteLoaders = sprites.map((s, i) => {
    const key = (s.name || `sprite${i}`).replace(/\s+/g, "_");
    return `    this.textures.addBase64('${key}', '${s.data_url}');`;
  }).join("\n") || "    // no sprites";

  const playerTex = sprites.length > 0
    ? (sprites[0].name || "sprite0").replace(/\s+/g,"_")
    : null;

  const userCode = files.map(f => `// ${f.path}\n${f.content || ""}`).join("\n\n");
  const enemyCount = Math.min(entities.filter(e => e.type === "enemy").length || 3, 8);

  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <title>${name}</title>
  <script src="https://cdn.jsdelivr.net/npm/phaser@3.80.1/dist/phaser.min.js"></script>
  <style>*{margin:0;padding:0;box-sizing:border-box}body{background:#09090b;display:flex;align-items:center;justify-content:center;height:100vh}</style>
</head>
<body>
<script>
${userCode}

class Boot extends Phaser.Scene {
  constructor(){super('Boot')}
  preload(){
${spriteLoaders}
  }
  create(){this.scene.start('Game')}
}

class Game extends Phaser.Scene {
  constructor(){super('Game')}
  create(){
    const W=this.scale.width, H=this.scale.height;
    this.platforms=this.physics.add.staticGroup();
    const g=this.add.rectangle(W/2,H-16,W,32,0x334155);
    this.physics.add.existing(g,true);
    this.platforms.add(g);
    this.player=this.physics.add.sprite(100,H-60,${playerTex ? `'${playerTex}'` : "'__fb'"});
    this.player.setCollideWorldBounds(true);
    this.physics.add.collider(this.player,this.platforms);
    this.keys=this.input.keyboard.addKeys({
      left:'${left}',right:'${right}',up:'${up}',jump:'${jump==="Space"?"SPACE":jump}'
    });
  }
  update(){
    const g=this.player.body.blocked.down;
    if(this.keys.left.isDown)this.player.setVelocityX(-200);
    else if(this.keys.right.isDown)this.player.setVelocityX(200);
    else this.player.setVelocityX(0);
    if((this.keys.jump.isDown||this.keys.up.isDown)&&g)this.player.setVelocityY(-450);
  }
}

new Phaser.Game({
  type:Phaser.AUTO, width:800, height:480, backgroundColor:'#09090b',
  physics:{default:'arcade',arcade:{gravity:{y:500},debug:false}},
  scene:[Boot,Game]
});
</script>
</body>
</html>`;
}

// ---------------------------------------------------------------------------
// UI
// ---------------------------------------------------------------------------
const TARGETS = [
  { id: "phaser", label: "Phaser 3",   ext: ".js",   lang: "javascript" },
  { id: "godot",  label: "Godot 4",    ext: ".gd",   lang: "gdscript"   },
  { id: "unity",  label: "Unity 2D",   ext: ".cs",   lang: "csharp"     },
  { id: "html",   label: "Standalone HTML", ext: ".html", lang: "html"  },
];

export default function ExportPanel({ project }) {
  const { t } = useI18n();
  const [activeTarget, setActiveTarget] = useState("phaser");
  const [copied, setCopied] = useState(false);

  const generated = useMemo(() => {
    switch (activeTarget) {
      case "phaser": return generatePhaser(project);
      case "godot":  return generateGodot(project);
      case "unity":  return generateUnity(project);
      case "html":   return generateStandaloneHTML(project);
      default: return "";
    }
  }, [activeTarget, project]);

  const target = TARGETS.find(t => t.id === activeTarget);
  const filename = `${project.slug || "game"}${target.ext}`;

  const handleDownload = () => {
    const mime = activeTarget === "html" ? "text/html" : "text/plain";
    const blob = new Blob([generated], { type: mime });
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement("a");
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
    toast.success(`Descargado: ${filename}`);
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generated).then(() => {
      setCopied(true);
      toast.success("Copiado al portapapeles");
      setTimeout(() => setCopied(false), 2000);
    });
  };

  // Diagnostics
  const sprites  = project.sprites || [];
  const files    = project.files   || [];
  const entities = project.gdd?.entities || [];
  const warnings = [];
  if (sprites.length === 0) warnings.push("Sin sprites — los personajes se verán como rectángulos");
  if (files.length === 0)   warnings.push("Sin archivos de código — solo se genera el boilerplate");
  if (entities.length === 0) warnings.push("Sin entidades en el GDD — estadísticas por defecto");

  return (
    <div className="p-6 max-w-5xl mx-auto flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="text-[10px] tracking-[0.4em] uppercase text-cyan-400">
          // EXPORT — CÓDIGO REAL DEL PROYECTO
        </div>
        <div className="flex gap-2">
          <Button
            onClick={handleCopy}
            variant="ghost"
            className="rounded-none text-zinc-300 border border-zinc-800 hover:bg-zinc-900 gap-2"
          >
            {copied ? <CheckCircle size={14} className="text-green-400" /> : <Copy size={14} />}
            {copied ? "Copiado" : "Copiar"}
          </Button>
          <Button
            data-testid="export-download-btn"
            onClick={handleDownload}
            className="bg-cyan-400 hover:bg-cyan-300 text-zinc-950 rounded-none font-semibold gap-2"
          >
            <DownloadSimple size={14} /> Descargar {filename}
          </Button>
        </div>
      </div>

      {/* Target selector */}
      <div className="flex gap-px bg-zinc-800 border border-zinc-800 w-fit">
        {TARGETS.map(tgt => (
          <button
            key={tgt.id}
            data-testid={`export-target-${tgt.id}`}
            onClick={() => setActiveTarget(tgt.id)}
            className={
              "px-4 py-2 text-xs font-mono tracking-[0.2em] uppercase transition-colors " +
              (activeTarget === tgt.id
                ? "bg-cyan-400 text-zinc-950 font-semibold"
                : "bg-zinc-950 text-zinc-400 hover:bg-zinc-900")
            }
          >
            {tgt.label}
          </button>
        ))}
      </div>

      {/* Warnings */}
      {warnings.length > 0 && (
        <div className="border border-yellow-900/50 bg-yellow-950/30 p-3 space-y-1">
          {warnings.map((w, i) => (
            <div key={i} className="flex items-start gap-2 text-xs font-mono text-yellow-400">
              <Warning size={13} className="mt-0.5 flex-shrink-0" />
              {w}
            </div>
          ))}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-4 gap-px bg-zinc-800 border border-zinc-800">
        {[
          { label: "Sprites incluidos", value: sprites.length },
          { label: "Archivos de código", value: files.length },
          { label: "Entidades GDD", value: entities.length },
          { label: "Target", value: target.label },
        ].map(item => (
          <div key={item.label} className="bg-zinc-950 p-3">
            <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">{item.label}</div>
            <div className="font-mono text-sm text-cyan-400 mt-1">{item.value}</div>
          </div>
        ))}
      </div>

      {/* Code preview */}
      <div className="border border-zinc-800">
        <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-800 bg-zinc-900">
          <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">{filename}</span>
          <span className="text-[10px] font-mono text-zinc-600">
            {generated.split("\n").length} líneas · {(new Blob([generated]).size / 1024).toFixed(1)} KB
          </span>
        </div>
        <pre
          data-testid="export-code-preview"
          className="overflow-auto p-4 text-xs font-mono text-zinc-300 leading-relaxed bg-zinc-950 gf-scroll"
          style={{ maxHeight: 480 }}
        >
          <code>{generated}</code>
        </pre>
      </div>

      {/* Instructions per target */}
      <div className="border border-zinc-800 p-4 bg-zinc-900/40">
        <div className="text-[10px] tracking-[0.4em] uppercase text-zinc-500 mb-3">
          // INSTRUCCIONES DE INTEGRACIÓN
        </div>
        <ExportInstructions target={activeTarget} project={project} />
      </div>
    </div>
  );
}

function ExportInstructions({ target, project }) {
  const s = {
    fontSize: "12px",
    fontFamily: "'JetBrains Mono', monospace",
    color: "var(--tw-zinc-300, #d4d4d8)",
    lineHeight: 1.7,
  };
  const steps = {
    phaser: [
      "1. Crea index.html con <script src='phaser.min.js'></script>",
      "2. Copia el archivo .js generado junto al index.html",
      `3. Copia tus sprites (${project.sprites?.length || 0} PNGs) desde el Sprite Editor`,
      "4. Abre index.html en el navegador — no necesitas servidor",
      "5. Para publicar: sube todo a itch.io como HTML game",
    ],
    godot: [
      "1. Godot 4 → New Project → 2D",
      "2. Copia Player.gd a res://scripts/",
      `3. Importa tus ${project.sprites?.length || 0} sprites PNG a res://sprites/`,
      "4. Project Settings → Input Map → agrega: move_left, move_right, jump",
      "5. Adjunta el script al nodo CharacterBody2D del jugador",
    ],
    unity: [
      "1. Unity Hub → New → 2D (URP)",
      "2. Copia los .cs generados a Assets/Scripts/",
      `3. Importa tus ${project.sprites?.length || 0} sprites PNG a Assets/Sprites/`,
      "4. Crea GameObject 'Player' con Rigidbody2D + BoxCollider2D",
      "5. Arrastra PlayerController.cs al GameObject Player",
    ],
    html: [
      "1. El archivo .html es 100% autónomo — no necesita servidor",
      "2. Ábrelo directamente en Chrome/Firefox",
      "3. Para publicar en itch.io: sube el .html como HTML game",
      "4. Compatible con GitHub Pages, Netlify, cualquier hosting estático",
    ],
  };

  return (
    <ol style={s}>
      {(steps[target] || []).map((step, i) => (
        <li key={i} style={{ color: "#a1a1aa", marginBottom: 4 }}>{step}</li>
      ))}
    </ol>
  );
}